import { useState, useRef, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ChipCardOverlay } from "@/components/chip-card-overlay"

gsap.registerPlugin(ScrollTrigger)

const experiments = [
  {
    title: "End-to-End PD",
    medium: "Capability",
    description: "Full RTL-to-GDSII ownership of the physical design flow.",
    span: "col-span-2 row-span-2",
  },
  {
    title: "Methodology & Automation",
    medium: "Capability",
    description: "Custom flows and automation to accelerate every project.",
    span: "col-span-1 row-span-1",
  },
  {
    title: "Constraint Development",
    medium: "Capability",
    description: "Timing constraint development and management.",
    span: "col-span-1 row-span-1",
  },
  {
    title: "Full-Chip Implementation",
    medium: "Capability",
    description: "Power/PAD/BUMP planning and RDL routing at full-chip scale.",
    span: "col-span-1 row-span-1",
  },
  {
    title: "STA & Signoff",
    medium: "Capability",
    description: "Static timing, DRC/LVS and EM/IR closure.",
    span: "col-span-1 row-span-1",
  },
  {
    title: "Analog IP Integration",
    medium: "Capability",
    description: "Floorplanning and implementation of analog/mixed-signal IPs.",
    span: "col-span-1 row-span-1",
  },
  {
    title: "Partition & Hierarchical Impl.",
    medium: "Capability",
    description: "Block-level and hierarchical implementation strategies.",
    span: "col-span-1 row-span-1",
  },
  {
    title: "PPA Optimization",
    medium: "Capability",
    description: "Power, performance and area tuning across the flow.",
    span: "col-span-1 row-span-1",
  },
]

export function WorkSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !gridRef.current) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        },
      )

      const cards = gridRef.current?.querySelectorAll("article")
      if (cards && cards.length > 0) {
        gsap.set(cards, { y: 60, opacity: 0 })
        gsap.to(cards, {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        })
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="work" className="relative border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12">
      <div ref={headerRef} className="mb-16 flex items-end justify-between">
        <div className="max-w-3xl">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">Capability</span>
          <h2 className="mt-4 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-7xl">
            Full-Chip Physical Design,
            <br />
            End To End
          </h2>
          <p className="mt-6 font-mono text-xs leading-relaxed text-muted-foreground md:hidden">
            Eight core capabilities spanning methodology, implementation and signoff.
          </p>
        </div>
        <p className="hidden max-w-xs text-right font-mono text-xs leading-relaxed text-muted-foreground md:block">
          Eight core capabilities spanning methodology, implementation and signoff.
        </p>
      </div>

      <div
        ref={gridRef}
        className="grid grid-cols-2 gap-4 auto-rows-[180px] md:grid-cols-4 md:gap-6 md:auto-rows-[200px]"
      >
        {experiments.map((experiment, index) => (
          <WorkCard key={index} experiment={experiment} index={index} persistHover={index === 0} />
        ))}
      </div>
    </section>
  )
}

function WorkCard({
  experiment,
  index,
  persistHover = false,
}: {
  experiment: {
    title: string
    medium: string
    description: string
    span: string
  }
  index: number
  persistHover?: boolean
}) {
  const [isHovered, setIsHovered] = useState(false)
  const cardRef = useRef<HTMLElement>(null)
  const [isScrollActive, setIsScrollActive] = useState(false)
  const [tilt, setTilt] = useState({ x: 0, y: 0 })

  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!cardRef.current) return

    const rect = cardRef.current.getBoundingClientRect()
    const x = (event.clientX - rect.left) / rect.width - 0.5
    const y = (event.clientY - rect.top) / rect.height - 0.5

    setTilt({ x, y })
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
    setTilt({ x: 0, y: 0 })
  }, [])

  useEffect(() => {
    if (!persistHover || !cardRef.current) return

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: cardRef.current,
        start: "top 80%",
        onEnter: () => setIsScrollActive(true),
      })
    }, cardRef)

    return () => ctx.revert()
  }, [persistHover])

  const isActive = isHovered || isScrollActive
  const isLarge = experiment.span.includes("row-span-2")

  return (
    <article
      ref={cardRef}
      className={cn(
        "group relative flex cursor-pointer flex-col justify-between overflow-hidden border bg-card/90 p-5 transition-all duration-500",
        experiment.span,
        isActive ? "border-transparent shadow-[0_0_24px_oklch(0.7_0.2_45_/_0.12)]" : "border-border/40",
      )}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.y * -4}deg) rotateY(${tilt.x * 4}deg)`,
        transition: isHovered
          ? "transform 0.15s ease-out, border-color 0.4s, box-shadow 0.4s"
          : "transform 0.45s ease-out, border-color 0.4s, box-shadow 0.4s",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <ChipCardOverlay active={isActive} large={isLarge} />

      <div className="relative z-10">
        <span
          className={cn(
            "font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300",
            isActive ? "text-accent" : "text-muted-foreground",
          )}
        >
          {experiment.medium}
        </span>
        <h3
          className={cn(
            "mt-3 font-[var(--font-bebas)] text-xl tracking-tight uppercase transition-colors duration-300 md:text-2xl lg:text-3xl",
            isActive ? "text-accent" : "text-foreground",
          )}
        >
          {experiment.title}
        </h3>
      </div>

      <div className="relative z-10">
        <p
          className={cn(
            "max-w-[280px] font-mono text-xs leading-relaxed transition-all duration-500",
            isActive ? "translate-y-0 text-accent/75 opacity-100" : "translate-y-2 text-muted-foreground opacity-0",
          )}
        >
          {experiment.description}
        </p>
      </div>

      <span
        className={cn(
          "absolute bottom-4 right-4 z-10 font-mono text-[10px] transition-colors duration-300",
          isActive ? "text-accent/80" : "text-muted-foreground/40",
        )}
      >
        {String(index + 1).padStart(2, "0")}
      </span>

      <div
        className={cn(
          "absolute top-0 right-0 z-10 h-10 w-10 transition-opacity duration-500",
          isActive ? "opacity-100" : "opacity-0",
        )}
      >
        <div className="absolute top-0 right-0 h-px w-full bg-accent/80" />
        <div className="absolute top-0 right-0 h-full w-px bg-accent/80" />
      </div>
    </article>
  )
}
